import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronDown, CurrencyDollar } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import InputTextArea from 'src/components/InputTextArea';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { CURRENCY, currencyOptions, INTER_MODAL_TYPE_EQUIPMENT_TYPES } from 'src/constants/common';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { logError } from 'src/services/LoggingService';
import { formatDecimalNumber, collectFormErrorPaths, scrollToFirstFormError } from 'src/utils/CommonFunctions';
import * as yup from 'yup';

import { ADD_QUOTE_KEYS, INTER_MODAL_TYPE_OPTIONS } from '../../addQuote.const';
import {
    addQuoteShipmentDetails as shipmentDetailsSelector,
    addQuoteDivisionId as divisionIdSelector,
    addQuoteCustomerBillingAccountId as customerBillingAccountIdSelector,
} from '../../AddQuote.slice';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';
import { useRegisterStepperPersist } from '../useStepperPersist';

interface IFormData {
    primaryEquipmentType: string;
    primaryEquipmentTypeFullName: string;
    secondaryEquipmentTypes: string[];
    secondaryEquipmentTypeFullNames: string[];
    interModalType: string;
    isLoadInsuranceEnabled: boolean;
    insuranceCommodity: string[];
    insuranceCommercialValue: string;
    insuranceCurrencyType: string;
    specialNotes: string;
}

const validationSchema = yup.object().shape({
    primaryEquipmentType: yup.string().required('Primary equipment type is required'),
    primaryEquipmentTypeFullName: yup.string().required('Primary equipment type is required'),
    secondaryEquipmentTypes: yup.array().of(yup.string()).default([]),
    secondaryEquipmentTypeFullNames: yup.array().of(yup.string()).default([]),
    interModalType: yup.string().default('road'),
    insuranceCommodity: yup
        .array()
        .of(yup.string())
        .default([])
        .test('commodity-required-when-enabled', 'Commodity is required', function (value) {
            const { isLoadInsuranceEnabled } = this.parent;

            if (isLoadInsuranceEnabled) {
                return Array.isArray(value) && value.length > 0;
            }

            return true;
        }),
    insuranceCommercialValue: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .test('value-required-when-enabled', 'Insurance commercial value is required', function (value) {
            const { isLoadInsuranceEnabled } = this.parent;

            if (isLoadInsuranceEnabled) {
                return value !== null && value !== undefined && typeof value === 'number' && !isNaN(value);
            }

            return true;
        })
        .test('positive-number', 'Insurance commercial value must be a positive number', function (value) {
            if (value === null || value === undefined || isNaN(value)) {
                return true;
            }

            return value > 0;
        })
        .typeError('Insurance commercial value must be a valid number'),
    insuranceCurrencyType: yup.string().default(CURRENCY.CAD),
    specialNotes: yup.string().max(200, 'Special notes cannot exceed 200 characters').default(''),
});

interface StepShipmentDetailsProps {
    setHeaderChildren?: (children: React.ReactNode) => void;
}

const ShipmentDetails: React.FC<StepShipmentDetailsProps> = ({ setHeaderChildren }) => {
    const { t } = useTranslation();
    const shipmentDetails = useAppSelector(shipmentDetailsSelector);
    const currentUser = useAppSelector(currentUserSelector);
    const divisionId = useAppSelector(divisionIdSelector);
    const customerBillingAccountId = useAppSelector(customerBillingAccountIdSelector);
    const { onSaveNext, onBack, onPersist } = useSaveNextStep();

    const defaultCurrency = useMemo(() => {
        const selectedDivision: any = currentUser?.divisions?.find((divisionData) => divisionData?.id === divisionId);
        const selectedBillingAccount = selectedDivision?.customerBillingAccount?.find((billingData) => billingData?.id === customerBillingAccountId);

        return selectedBillingAccount?.currency?.toUpperCase();
    }, [currentUser, divisionId]);

    const { isVehicleTypeLoading, vehicleTypeOptions, commodityOptions } = useSelector((state: RootState) => state.basic);

    const [commodityInputValue, setCommodityInputValue] = useState<string>('');
    const [filteredCommodityOptions, setFilteredCommodityOptions] = useState<any>([]);
    const [isCommodityMenuOpen, setIsCommodityMenuOpen] = useState<boolean>(false);
    const [commoditySelectKey, setCommoditySelectKey] = useState<number>(0);
    const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(false);

    const [currencyRates, setCurrencyRates] = useState<{
        cadRate: number;
        usdRate: number;
    }>({
        cadRate: 1,
        usdRate: 0.75,
    });
    const prevInsuranceCurrencyRef = useRef<string | null>(null);
    const isConvertingCurrencyRef = useRef<boolean>(false);

    const defaultValues: IFormData = useMemo(() => {
        const parseToArray = (value: any, separator: string = '|'): string[] => {
            if (!value) return [];

            if (Array.isArray(value)) return value.filter((item: string) => item?.trim() !== '');

            if (typeof value === 'string') return value.split(separator).filter((item: string) => item.trim() !== '');

            return [];
        };

        return {
            primaryEquipmentType: shipmentDetails?.primaryEquipmentType || 'V',
            primaryEquipmentTypeFullName: shipmentDetails?.primaryEquipmentTypeFullName || 'Dry Van',
            secondaryEquipmentTypeFullNames: parseToArray(shipmentDetails?.secondaryEquipmentTypeFullNames, ','),
            secondaryEquipmentTypes: parseToArray(shipmentDetails?.secondaryEquipmentTypes, ','),
            interModalType: shipmentDetails?.interModalType || 'both',
            insuranceCommercialValue: shipmentDetails?.insuranceCommercialValue != null && Number(shipmentDetails.insuranceCommercialValue) > 0 ? String(shipmentDetails.insuranceCommercialValue) : '',
            isLoadInsuranceEnabled: !!(shipmentDetails?.isLoadInsuranceEnabled && shipmentDetails?.insuranceCommercialValue != null && Number(shipmentDetails.insuranceCommercialValue) > 0),
            insuranceCommodity: parseToArray(shipmentDetails?.insuranceCommodity),
            insuranceCurrencyType: shipmentDetails?.insuranceCurrencyType || (defaultCurrency ?? CURRENCY.CAD),
            specialNotes: shipmentDetails?.specialNotes || '',
        };
    }, [shipmentDetails, defaultCurrency]);

    const {
        control,
        watch,
        setValue,
        handleSubmit,
        getValues,
        trigger,
        clearErrors,
        formState: { isSubmitted },
    } = useForm({
        resolver: yupResolver(validationSchema) as any,
        defaultValues,
    });

    const primaryEquipmentType = watch('primaryEquipmentType');
    const primaryEquipmentTypeFullName = watch('primaryEquipmentTypeFullName');
    const secondaryEquipmentTypes = watch('secondaryEquipmentTypes');
    const isLoadInsuranceEnabled = watch('isLoadInsuranceEnabled');
    const insuranceCommercialValue = watch('insuranceCommercialValue');
    const insuranceCurrencyType = watch('insuranceCurrencyType');

    useEffect(() => {
        getPricingRate()
            .then((response: any) => {
                if (response?.data) {
                    setCurrencyRates({
                        cadRate: response.data.cadRate || 1,
                        usdRate: response.data.usdRate || 0.75,
                    });
                }
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error while fetching pricing rates',
                    errorLocation: 'ShipmentDetails: getPricingRate',
                    storeInDatabase: true,
                    logToConsole: true,
                });
            });
    }, []);

    const convertCurrency = useCallback(
        (value: number, fromCurrency: string, toCurrency: string): number => {
            if (!value || value === 0 || fromCurrency === toCurrency) {
                return formatDecimalNumber(value || 0, 2);
            }

            let convertedValue = 0;

            if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
                convertedValue = value * (currencyRates.usdRate || 0.75);
            } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
                convertedValue = value * (currencyRates.cadRate || 1);
            } else {
                convertedValue = value;
            }

            return formatDecimalNumber(convertedValue, 2);
        },
        [currencyRates]
    );

    // Initialize the previous currency ref on first render
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current === null && insuranceCurrencyType) {
            prevInsuranceCurrencyRef.current = insuranceCurrencyType;
        }
    }, [insuranceCurrencyType]);

    // Auto-convert commercial value when currency changes
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current && prevInsuranceCurrencyRef.current !== insuranceCurrencyType && insuranceCommercialValue && !isConvertingCurrencyRef.current) {
            isConvertingCurrencyRef.current = true;
            const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const convertedValue = convertCurrency(numericValue, prevInsuranceCurrencyRef.current, insuranceCurrencyType);
                setValue('insuranceCommercialValue', String(convertedValue));

                if (isSubmitted) {
                    trigger('insuranceCommercialValue');
                }
            }

            setTimeout(() => {
                isConvertingCurrencyRef.current = false;
            }, 100);
        }
        prevInsuranceCurrencyRef.current = insuranceCurrencyType;
    }, [insuranceCurrencyType, insuranceCommercialValue, convertCurrency, setValue, trigger, isSubmitted]);

    const onSubmit = useCallback(
        (data: any) => {
            const isInterModalApplicable = INTER_MODAL_TYPE_EQUIPMENT_TYPES.some((type) => data.primaryEquipmentTypeFullName?.toLowerCase().includes(type));

            const shipmentDetailsPayload = {
                ...data,
                interModalType: isInterModalApplicable ? data.interModalType : null,
                secondaryEquipmentTypes: data.secondaryEquipmentTypes?.join(','),
                secondaryEquipmentTypeFullNames: data.secondaryEquipmentTypeFullNames?.join(','),
                insuranceCommodity: data.insuranceCommodity?.length > 0 ? data.insuranceCommodity?.join('|') : null,
                specialNotes: data.specialNotes || null,
            };
            onSaveNext(shipmentDetailsPayload, ADD_QUOTE_KEYS.SHIPMENT_DETAILS);
        },
        [onSaveNext]
    );

    // Scroll the first invalid field into view when Next is blocked. RHF's onInvalid callback is
    // the only place the freshly-computed errors are available synchronously — a closure over
    // formState.errors would still hold the value from before validation ran.
    const onInvalid = useCallback((formErrors: any) => {
        scrollToFirstFormError(collectFormErrorPaths(formErrors));
    }, []);

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) {
            handleSubmit(() => {}, onInvalid)();

            return false;
        }
        handleSubmit(onSubmit)();
    }, [trigger, handleSubmit, onSubmit, onInvalid]);

    // Shared by Back and by the sidebar persist path — both leave the step WITHOUT yup
    // validation, so the array fields are normalised defensively (a half-filled form can still
    // hold arrays where onSubmit would have produced joined strings).
    const buildShipmentDetailsPayload = useCallback(
        (data: any) => ({
            ...data,
            secondaryEquipmentTypes: Array.isArray(data.secondaryEquipmentTypes) ? data.secondaryEquipmentTypes.join(',') : data.secondaryEquipmentTypes || '',
            secondaryEquipmentTypeFullNames: Array.isArray(data.secondaryEquipmentTypeFullNames) ? data.secondaryEquipmentTypeFullNames.join(',') : data.secondaryEquipmentTypeFullNames || '',
            insuranceCommodity: Array.isArray(data.insuranceCommodity) && data.insuranceCommodity.length > 0 ? data.insuranceCommodity.join('|') : null,
            specialNotes: data.specialNotes || null,
        }),
        []
    );

    const handleBack = useCallback(() => {
        onBack(buildShipmentDetailsPayload(getValues()), ADD_QUOTE_KEYS.SHIPMENT_DETAILS);
    }, [getValues, onBack, buildShipmentDetailsPayload]);

    useRegisterStepperPersist(() => {
        onPersist(buildShipmentDetailsPayload(getValues()), ADD_QUOTE_KEYS.SHIPMENT_DETAILS);
    });

    // Handler for primary equipment type change
    const handlePrimaryEquipmentChange = (onChange: any) => (event: any) => {
        onChange(event?.label);
        setValue('primaryEquipmentType', event?.value);

        // Remove primary from secondary if it exists
        const currentSecondaryTypes = secondaryEquipmentTypes || [];
        const filteredTypes = currentSecondaryTypes.filter((type: string) => type !== event?.value);
        setValue('secondaryEquipmentTypes', filteredTypes);

        const currentSecondaryNames = watch('secondaryEquipmentTypeFullNames') || [];
        const filteredNames = currentSecondaryNames.filter((name: string) => name !== event?.label);
        setValue('secondaryEquipmentTypeFullNames', filteredNames);
    };

    // Handler for secondary equipment type checkbox change
    const handleSecondaryEquipmentChange = (vehicleType: any) => (e: any) => {
        const currentSecondaryTypes = secondaryEquipmentTypes || [];
        const currentSecondaryNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (e.target.checked) {
            setValue('secondaryEquipmentTypes', [...currentSecondaryTypes, e.target.value]);
            setValue('secondaryEquipmentTypeFullNames', [...currentSecondaryNames, vehicleType.label]);
        } else {
            setValue(
                'secondaryEquipmentTypes',
                currentSecondaryTypes.filter((item: string) => item !== e.target.value)
            );
            setValue(
                'secondaryEquipmentTypeFullNames',
                currentSecondaryNames.filter((item: string) => item !== vehicleType.label)
            );
        }
    };

    const handleInsuranceCommodityMultipleChange = (onChange: any) => (selectedOptions: any) => {
        if (selectedOptions && Array.isArray(selectedOptions)) {
            const values = selectedOptions.map((option: any) => option.value);
            onChange(values);
        } else {
            onChange([]);
        }

        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
        }
    };

    const handleCreateCommodityOption = (onChange: any) => (inputValue: string) => {
        const trimmedValue = inputValue?.trim();

        if (trimmedValue) {
            const currentValue = watch('insuranceCommodity') || [];
            const newValue = Array.isArray(currentValue) ? [...currentValue, trimmedValue] : [trimmedValue];
            onChange(newValue);

            setCommodityInputValue('');
            setFilteredCommodityOptions(commodityOptions);

            setCommoditySelectKey((prev) => prev + 1);

            if (isSubmitted) {
                trigger('insuranceCommodity');
            }
        }
    };

    const handleCommodityInputChange = (inputValue: string, actionMeta: any) => {
        if (actionMeta.action === 'input-change') {
            setCommodityInputValue(inputValue);

            if (!inputValue.trim()) {
                setFilteredCommodityOptions(commodityOptions);
            } else {
                const filtered = commodityOptions.filter((option: any) => {
                    const label = typeof option === 'string' ? option : option.label || '';
                    const value = typeof option === 'string' ? option : option.value || '';

                    return (
                        (label && typeof label === 'string' && label.toLowerCase().includes(inputValue.toLowerCase())) ||
                        (value && typeof value === 'string' && value.toLowerCase().includes(inputValue.toLowerCase()))
                    );
                });
                setFilteredCommodityOptions(filtered);
            }
        } else if (actionMeta.action === 'menu-close') {
            setCommodityInputValue('');
        }
    };

    const handleCommodityMenuOpen = () => {
        setIsCommodityMenuOpen(true);
        setFilteredCommodityOptions(commodityOptions);
        setCommodityInputValue('');
    };

    const handleCommodityMenuClose = () => {
        setIsCommodityMenuOpen(false);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleClearCommodities = (onChange: any) => () => {
        setIsCommodityMenuOpen(false);
        onChange([]);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleCommodityKeyDown = useCallback(
        (onChange: any, currentValue: string[]) => (event: React.KeyboardEvent) => {
            if (event.key === 'Backspace' && !commodityInputValue && Array.isArray(currentValue) && currentValue.length > 0) {
                event.preventDefault();
                const newValue = currentValue.slice(0, -1);
                onChange(newValue);

                if (isSubmitted) {
                    trigger('insuranceCommodity');
                }
            }
        },
        [commodityInputValue, isSubmitted, trigger]
    );

    // Handler for insurance commercial value change
    const handleInsuranceCommercialValueChange = (onChange: any) => (e: any) => {
        onChange(e.target.value);

        if (isSubmitted) {
            trigger('insuranceCommodity');
        }
    };

    // Handler for insurance currency type change
    const handleInsuranceCurrencyChange = (onChange: any) => (selectedOption: any) => {
        onChange(selectedOption?.value || CURRENCY.USD);
    };

    const handleLoadInsuranceToggle = (onChange: any) => (e: any) => {
        onChange(e.target.checked);

        if (!e.target.checked) {
            setValue('insuranceCommercialValue', '');

            clearErrors(['insuranceCommercialValue', 'insuranceCommodity']);
        }

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
            trigger('insuranceCommodity');
        }
    };

    useEffect(() => {
        if (commodityOptions && commodityOptions.length > 0) {
            setFilteredCommodityOptions(commodityOptions);
        }
    }, [commodityOptions]);

    useEffect(() => {
        setHeaderChildren?.(<StepperAction handleNext={handleNext} handleBack={handleBack} nextButtonText={t('Common.Next')} />);

        return () => {
            setHeaderChildren?.(null);
        };
    }, [handleNext, handleBack, t]);

    return (
        <>
            {/* Primary Equipment Type */}
            <div data-field-anchor="primaryEquipmentType primaryEquipmentTypeFullName interModalType" className="stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        Primary Equipment Type
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="primaryEquipmentTypeFullName"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <SelectBox
                                required
                                name="primaryEquipmentType"
                                placeholder="Select equipment type"
                                label=""
                                labelClassName="form_label mb-1.5 block"
                                options={vehicleTypeOptions}
                                value={vehicleTypeOptions.find((item: any) => value === item.label)}
                                isLoading={isVehicleTypeLoading}
                                isMultipleSelection={false}
                                onChangeFunc={handlePrimaryEquipmentChange(onChange)}
                                errorText={error?.message ? true : false}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Secondary Equipment Type */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Secondary Equipment Type</label>
                </div>
                <div className="flex-1 rounded-lg border border-utilityGray200 bg-white p-1.5 flex flex-wrap relative">
                    <div className="absolute right-4 top-3">
                        <TooltipCmp message={isEquipmentExpanded ? t('Common.Collapse') : t('Common.Expand')} childrenClassName="!flex cursor-pointer">
                            <div onClick={() => setIsEquipmentExpanded((prev) => !prev)}>
                                <ChevronDown className={`w-4 h-4 text-grayLight600 transition-transform duration-300 ${isEquipmentExpanded ? 'rotate-180' : 'rotate-0'}`} />
                            </div>
                        </TooltipCmp>
                    </div>
                    {vehicleTypeOptions
                        .filter((vehicleType: any) => vehicleType.value !== primaryEquipmentType)
                        .slice(0, 3)
                        .map((vehicleType: any, index: number) => (
                            <div className="w-1/3 p-1.5 flex items-center" key={`equipment_type_${index}`}>
                                <CheckBox
                                    id={`${vehicleType.value}`}
                                    name={vehicleType.label}
                                    checked={primaryEquipmentType === vehicleType.value || secondaryEquipmentTypes?.includes(vehicleType.value)}
                                    parentClassName="!w-3.5 !h-3.5"
                                    value={vehicleType.value}
                                    onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                    disabled={false}
                                />
                                <label htmlFor={`${vehicleType.value}`} className="text-textSecondary text-xs font-medium pl-1.5 cursor-pointer">
                                    {vehicleType.label}
                                </label>
                            </div>
                        ))}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateRows: isEquipmentExpanded ? '1fr' : '0fr',
                            transition: 'grid-template-rows 300ms ease',
                            width: '100%',
                        }}
                    >
                        <div className="overflow-hidden flex flex-wrap">
                            {vehicleTypeOptions
                                .filter((vehicleType: any) => vehicleType.value !== primaryEquipmentType)
                                .slice(3)
                                .map((vehicleType: any, index: number) => (
                                    <div className="w-1/3 p-1.5 flex items-center" key={`equipment_type_extra_${index}`}>
                                        <CheckBox
                                            id={`${vehicleType.value}`}
                                            name={vehicleType.label}
                                            checked={primaryEquipmentType === vehicleType.value || secondaryEquipmentTypes?.includes(vehicleType.value)}
                                            parentClassName="!w-3.5 !h-3.5"
                                            value={vehicleType.value}
                                            onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                            disabled={false}
                                        />
                                        <label htmlFor={`${vehicleType.value}`} className="text-textSecondary text-xs font-medium pl-1.5 cursor-pointer">
                                            {vehicleType.label}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>

            {INTER_MODAL_TYPE_EQUIPMENT_TYPES.some((type) => primaryEquipmentTypeFullName?.toLowerCase().includes(type)) && (
                <div className="border-t border-utilityGray200 stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common">{t('Common.Mode')}</label>
                    </div>
                    <div className="flex-1">
                        <Controller
                            name="interModalType"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <div className="flex flex-wrap gap-2">
                                    {INTER_MODAL_TYPE_OPTIONS.map((option) => {
                                        const IconComponent = option.icon;

                                        return (
                                            <ButtonCmp
                                                key={option.value}
                                                type="button"
                                                className={value === option.value ? 'btn-outline-primary' : 'btn_secondary_black'}
                                                onClick={() => onChange(option.value)}
                                            >
                                                <IconComponent className="w-4 h-4" />
                                                {option.label}
                                            </ButtonCmp>
                                        );
                                    })}
                                </div>
                            )}
                        />
                    </div>
                </div>
            )}

            {/* Commodity Type */}
            <div className="border-t border-utilityGray200 stepper-content-container !max-w-[825px]">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Commodity Type</label>
                </div>
                <div className="flex-1 flex max-w-[590px] flex-col">
                    <Controller
                        name="insuranceCommodity"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            const selectedOptions =
                                (Array.isArray(value) ? value : [])
                                    .map((val: string) => {
                                        const findValue = commodityOptions.find((option: any) => option.value === val);

                                        if (findValue) {
                                            return findValue;
                                        }

                                        if (val && val.trim()) {
                                            return {
                                                value: val,
                                                label: val,
                                            };
                                        }

                                        return null;
                                    })
                                    .filter(Boolean) || [];

                            const optionsToDisplay = filteredCommodityOptions.length > 0 || commodityInputValue.trim() ? filteredCommodityOptions : commodityOptions;

                            return (
                                <SelectBox
                                    key={`commodity-select-${commoditySelectKey}`}
                                    SelectDropdownWrapClass="multi-select-v2-dropdown"
                                    classComp={`!bg-white`}
                                    options={optionsToDisplay}
                                    value={selectedOptions}
                                    onChangeFunc={handleInsuranceCommodityMultipleChange(onChange)}
                                    onInputChange={handleCommodityInputChange}
                                    placeholder={t('Common.Select_commodity')}
                                    errorText={error?.message ? true : false}
                                    isClearable={true}
                                    isMultipleSelection={true}
                                    closeMenuOnSelect={false}
                                    onClearFunc={handleClearCommodities(onChange)}
                                    isMultipleEquipmentType={true}
                                    maxVisibleValues={8}
                                    keepMenuOpenOnMultiSelect={true}
                                    autoCompleteSelect={true}
                                    onCreateOption={handleCreateCommodityOption(onChange)}
                                    isValidNewOption={Boolean(commodityInputValue && filteredCommodityOptions.length === 0)}
                                    createOptionText="Add new commodity"
                                    isSearchable={true}
                                    forceSearchable={true}
                                    noOptionMessage="No options found"
                                    onMenuOpen={handleCommodityMenuOpen}
                                    onMenuClose={handleCommodityMenuClose}
                                    menuIsOpen={isCommodityMenuOpen}
                                    maintainFocusOnClear={true}
                                    isShowInputValue={true}
                                    defaultValueInput={commodityInputValue}
                                    isHideCheckbox
                                    onKeyDown={handleCommodityKeyDown(onChange, value || [])}
                                />
                            );
                        }}
                    />
                </div>
            </div>

            {/* Load Insurance */}
            <div data-field-anchor="insuranceCommercialValue insuranceCommodity isLoadInsuranceEnabled insuranceCurrencyType" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex items-center justify-between">
                    <Controller
                        name="isLoadInsuranceEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                isChecked={value || false}
                                onChange={handleLoadInsuranceToggle(onChange)}
                                label={t('Common.Load_Insurance')}
                                switchClassName="cursor-pointer"
                                className=""
                                name="isLoadInsuranceEnabled"
                                labelPosition="left"
                                id="isLoadInsuranceEnabled"
                                isDisabled={false}
                            />
                        )}
                    />
                </div>
                <div className="stepper-lable-content-wrap flex-1">
                    <div className="flex-wrap flex gap-x-2">
                        <Controller
                            name="insuranceCommercialValue"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputType="number"
                                    value={value}
                                    onChangeFunc={handleInsuranceCommercialValueChange(onChange)}
                                    placeholder="Enter commercial value"
                                    isError={!!error}
                                    disabled={!isLoadInsuranceEnabled}
                                    className="pl-8"
                                    shouldFormatNumber={true}
                                    maxLength={8}
                                    icon={<CurrencyDollar className="input-currency-sign" />}
                                />
                            )}
                        />

                        <Controller
                            name="insuranceCurrencyType"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <SelectBox
                                    options={currencyOptions}
                                    value={currencyOptions.find((option) => option.value === value)}
                                    onChangeFunc={handleInsuranceCurrencyChange(onChange)}
                                    parentClassName="min-w-[80px]"
                                    isDisabled={!isLoadInsuranceEnabled}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            {/* Special Notes */}
            <div data-field-anchor="specialNotes" className="stepper-content-container border-t pb-5 border-utilityGray200 border-b">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Special Instructions</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="specialNotes"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputTextArea value={value} onChangeFunc={onChange} placeholder="Enter any special instructions here" errorText={error?.message} maxLength={200} rows={4} />
                        )}
                    />
                </div>
            </div>
        </>
    );
};

export default ShipmentDetails;
